import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { userDataService } from '../services/userDataService';
import { defaultSetService } from '../services/defaultSetService';
import ConfirmModal from './ConfirmModal';

interface LevelSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  classId: string;
}

// 기존 PointsTablePage의 스타일과 동일하게 구성
const LevelSettingsModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: block;
  z-index: 1000;
  overflow-y: auto;
  padding: 80px 20px;
  min-height: 100vh;

  @media (max-width: 600px) {
    padding: 60px 20px;
  }
`;

const LevelSettingsModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  margin: 0 auto;
  overflow: visible;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  margin-bottom: 0;
  border-bottom: 1px solid var(--color-border);

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text-gray);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--color-panel);
  }
`;

const LevelSettingsBody = styled.div`
  padding: 24px;
`;

const LevelSettingsSection = styled.div`
  margin-top: 20px;
  background: var(--color-section-bg);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--color-section-border);

  &:first-of-type {
    margin-top: 0;
  }
`;

const LevelSettingsSectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  color: var(--color-settings-section-title);
  font-weight: 600;
  background: var(--color-section-title-bg);
  padding: 4px 16px;
  border: 1px solid var(--color-section-title-border);
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 20px;
  text-align: center;
  width: 100%;
`;

const LevelSettingsDivider = styled.hr`
  border: none;
  height: 1px;
  background-color: var(--color-border);
  margin: 20px 0;
`;

const LevelSettingsFormGroup = styled.div`
  margin-bottom: 20px;
`;

const LevelSettingsLabel = styled.label<{ $isDisabled?: boolean }>`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$isDisabled ? 'var(--color-disabled-text)' : 'var(--color-text-primary)'};
  margin-bottom: 8px;
`;

const LevelPointInputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LevelPointMinusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const LevelPointInput = styled.input`
  padding: 8px 4px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  font-weight: normal;
  flex: 1;
  min-width: 0;
  text-align: center;
  height: 38px;
  line-height: 38px;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  /* number input의 기본 증감 버튼 제거 */
  -moz-appearance: textfield;
  
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const LevelPointPlusButton = styled.button`
  width: 28px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const LevelSettingsDescription = styled.p`
  font-size: 12px;
  color: var(--color-text-gray);
  margin-top: 8px;
  line-height: 1.4;
`;

const LevelSelectRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 24px;
`;

const LevelSelectMinusButton = styled.button`
  width: 32px;
  height: 32px;
  background: var(--color-panel);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
  }
`;

const LevelSelectPlusButton = styled.button`
  width: 32px;
  height: 32px;
  background: var(--color-panel);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
  }
`;

const LevelSelectDisplay = styled.div<{ $level: number }>`
  width: 130px;
  padding: 8px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  border-radius: 8px;
  border: 2px solid transparent;
  box-sizing: border-box;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-border);
  }
`;

const LevelSelectInput = styled.input`
  width: 130px;
  padding: 8px 20px;
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  text-align: center;
  background: var(--color-surface);
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;

  &:focus {
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.2);
  }
`;

const LevelSettingsModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 100%;
`;

const LevelSettingsCloseButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: transparent;
  color: var(--color-accent);
  border: 1px solid var(--color-accent);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
    border-color: var(--color-accent);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }
`;

const LevelSettingsSaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-accent)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 레벨별 보상 통합 컨테이너 (코인 + 아이템)
const LevelRewardContainer = styled.div<{ $level: number }>`
  margin-top: 20px;
  padding: 20px;
  background: var(--color-panel);
  border: 4px solid ${props => getLevelColor(props.$level)};
  border-radius: 12px;
  text-align: center;
`;

const LevelRewardSubTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 10px 0;
  padding: 6px 20px;
  text-align: center;
  background: rgba(128, 128, 128, 0.08);
  border: none;
  border-radius: 9999px;
  display: inline-block;
`;

const LevelCoinRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const LevelCoinMinusButton = styled.button`
  width: 32px;
  height: 32px;
  background: var(--color-panel);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
  }
`;

const LevelCoinDisplay = styled.div`
  width: 130px;
  padding: 8px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: #f39c12;
`;

const LevelCoinPlusButton = styled.button`
  width: 32px;
  height: 32px;
  background: var(--color-panel);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
  }
`;

const ItemCardSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 120px);
  gap: 14px;
  justify-content: center;
  margin-top: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const ItemCardSelectBlock = styled.div<{ $isSelected?: boolean }>`
  width: 120px;
  height: 200px;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 8px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;

  /* 비선택 시: 회색톤, 약간 불투명, 비활성화 느낌 */
  ${props => !props.$isSelected && `
    opacity: 0.65;
    filter: saturate(0.4);
  `}

  /* 다크 모드: 비선택 시 진한 회색 테두리, 선택 시 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid ${props => props.$isSelected ? 'rgba(255, 255, 255, 0.8)' : 'var(--color-border)'};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ItemCardIconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  border: 2px solid var(--color-accent-strong);

  /* 다크 모드: 진한 회색 테두리 (비선택 카드와 통일) */
  :root:not([data-theme='light']) & {
    border: 2px solid var(--color-accent-strong);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemCardName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemCardMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;

const ItemCardMetaGroup = styled.div`
  width: 100%;
  margin-top: 6%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ItemCardCost = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;

  svg {
    width: 14px;
    height: 14px;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: #f39c12;
    line-height: 1;
  }
`;

const ItemCardUseCount = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;

  svg {
    width: 12px;
    height: 12px;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: #ff4757;
    line-height: 1;
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;

  /* 다크 모드: 원형은 흰색, v 표시는 진한 색 */
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.95);
    color: var(--color-accent-strong);
  }
`;

const EmptyItemCardMessage = styled.div`
  padding: 32px;
  margin-top: 16px;
  text-align: center;
  color: var(--color-text-gray);
  font-size: 14px;
  line-height: 1.6;
`;

// 레벨 색상 정의 (10개 색상으로 순환: 레벨 0,10,20...은 메인테마 초록/흰색, 나머지는 1~9 색상 반복)
const levelColors = [
  '#FC5555', // 레벨1, 11, 21... - 빨간색
  '#FF9933', // 레벨2, 12, 22... - 주황색
  '#CCCC00', // 레벨3, 13, 23... - 노란색
  '#669900', // 레벨4, 14, 24... - 연두색
  '#119473', // 레벨5, 15, 25... - 청록색
  '#0066CC', // 레벨6, 16, 26... - 파란색
  '#9900CC', // 레벨7, 17, 27... - 보라색
  '#920049', // 레벨8, 18, 28... - 진한 분홍색
  '#D60093'  // 레벨9, 19, 29... - 분홍색
];

// 레벨별 색상 가져오기 (10개 색상으로 순환)
const getLevelColor = (level: number): string => {
  // 레벨 0, 10, 20 등 (10의 배수)일 때는 메인테마 초록/흰색
  if (level % 10 === 0) {
    return 'var(--color-level-0)';
  }
  // 나머지 레벨은 1~9 색상 반복 (레벨 1→인덱스0, 레벨 2→인덱스1, ... 레벨 9→인덱스8)
  const colorIndex = (level % 10) - 1;
  return levelColors[colorIndex];
};

export const LevelSettingsModal: React.FC<LevelSettingsModalProps> = ({
  isOpen,
  onClose,
  userId,
  classId
}) => {
  const [levelUpPoints, setLevelUpPoints] = useState<number | ''>(50);
  const [selectedLevel, setSelectedLevel] = useState<number>(1);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [levelCoins, setLevelCoins] = useState<{ [level: number]: number }>({});
  const [levelItemCards, setLevelItemCards] = useState<{ [level: number]: string[] }>({});
  const [availableItemCards, setAvailableItemCards] = useState<any[]>([]);
  const [defaultPoints, setDefaultPoints] = useState<number>(50); // 클래스의 기본 포인트 단위
  const [coinDefaultPoints, setCoinDefaultPoints] = useState<number>(10); // 기본 코인 단위
  const [initialLevelUpPoints, setInitialLevelUpPoints] = useState<number | ''>(50);
  const [initialLevelCoins, setInitialLevelCoins] = useState<{ [level: number]: number }>({});
  const [initialLevelItemCards, setInitialLevelItemCards] = useState<{ [level: number]: string[] }>({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isEditingLevel, setIsEditingLevel] = useState(false);
  const [editingLevelValue, setEditingLevelValue] = useState<string>('1');

  const commitLevelEdit = useCallback(() => {
    const trimmed = editingLevelValue.trim();
    if (trimmed === '') {
      setSelectedLevel(1);
    } else {
      const v = parseInt(trimmed, 10);
      setSelectedLevel(!isNaN(v) && v >= 1 && v <= 100 ? v : 1);
    }
    setIsEditingLevel(false);
  }, [editingLevelValue]);

  const startLevelEdit = useCallback(() => {
    setEditingLevelValue(String(selectedLevel || 1));
    setIsEditingLevel(true);
  }, [selectedLevel]);

  const loadLevelSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('🔍 LevelSettingsModal: 레벨 설정 로드 시작', { userId, classId });

      // 사용자 정보 로드
      const userProfile = await userDataService.loadUserProfile(userId);
      setCurrentUser(userProfile);

      const settings = await userDataService.loadLevelSettings(userId, classId);
      console.log('🔍 LevelSettingsModal: 로드된 설정:', settings);
      
      if (settings) {
        const loadedLevelUpPoints = settings.levelUpPoints || 50;
        const loadedSelectedLevel = settings.selectedLevel || 1;
        const loadedLevelCoins = settings.levelCoins || {};
        const loadedLevelItemCards = settings.levelItemCards || {};
        
        setLevelUpPoints(loadedLevelUpPoints);
        setSelectedLevel(loadedSelectedLevel);
        setLevelCoins(loadedLevelCoins);
        setLevelItemCards(loadedLevelItemCards);
        
        // 초기 상태 저장
        setInitialLevelUpPoints(loadedLevelUpPoints);
        setInitialLevelCoins(JSON.parse(JSON.stringify(loadedLevelCoins)));
        setInitialLevelItemCards(JSON.parse(JSON.stringify(loadedLevelItemCards)));
        setShowCancelConfirm(false);
        
        console.log('🔍 LevelSettingsModal: 상태 설정 완료', {
          levelUpPoints: loadedLevelUpPoints,
          selectedLevel: loadedSelectedLevel,
          levelCoins: loadedLevelCoins,
          levelItemCards: loadedLevelItemCards
        });
      }
    } catch (error) {
      console.error('레벨 설정 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, [classId, userId]);

  const loadItemCards = useCallback(async () => {
    try {
      if (userId && classId) {
        const { ItemCardService } = await import('../services/itemCardService');
        const itemCardService = new ItemCardService(userId, classId);
        const items = await itemCardService.loadItemCardsByCategory('levelup');
        setAvailableItemCards(items);
      }
    } catch (error) {
      console.error('아이템 카드 로드 실패:', error);
    }
  }, [classId, userId]);

  const loadClassData = useCallback(async () => {
    try {
      if (userId && classId) {
        const classData = await userDataService.loadClass(userId, classId);
        if (classData?.assignmentSettings?.defaultPoints) {
          setDefaultPoints(classData.assignmentSettings.defaultPoints);
          console.log('✅ 클래스 기본 포인트 로드:', classData.assignmentSettings.defaultPoints);
        }
        const coinSettings = await defaultSetService.loadCoinSettings(userId, classId);
        if (coinSettings?.defaultPoints !== undefined) {
          setCoinDefaultPoints(coinSettings.defaultPoints);
          console.log('✅ 기본 코인 단위 로드:', coinSettings.defaultPoints);
        }
      }
    } catch (error) {
      console.error('클래스 데이터 로드 실패:', error);
    }
  }, [classId, userId]);

  // 레벨 설정 데이터 로드
  useEffect(() => {
    if (isOpen) {
      loadLevelSettings();
      loadItemCards();
      loadClassData();
    }
  }, [isOpen, loadClassData, loadItemCards, loadLevelSettings]);

  // 변경사항 감지 (selectedLevel은 UI 상태이므로 제외)
  const hasChanges = useMemo(() => {
    const currentLevelUpPoints = typeof levelUpPoints === 'number' ? levelUpPoints : 50;
    const initialLevelUpPointsNum = typeof initialLevelUpPoints === 'number' ? initialLevelUpPoints : 50;
    
    if (currentLevelUpPoints !== initialLevelUpPointsNum) return true;
    // selectedLevel은 단순히 현재 보고 있는 레벨을 나타내는 UI 상태이므로 변경사항 감지에서 제외
    
    // levelCoins 비교
    const currentCoinsKeys = Object.keys(levelCoins).map(Number).sort();
    const initialCoinsKeys = Object.keys(initialLevelCoins).map(Number).sort();
    if (currentCoinsKeys.length !== initialCoinsKeys.length) return true;
    if (currentCoinsKeys.some((key, idx) => key !== initialCoinsKeys[idx])) return true;
    if (currentCoinsKeys.some(key => levelCoins[key] !== initialLevelCoins[key])) return true;
    
    // levelItemCards 비교
    const currentItemCardsKeys = Object.keys(levelItemCards).map(Number).sort();
    const initialItemCardsKeys = Object.keys(initialLevelItemCards).map(Number).sort();
    if (currentItemCardsKeys.length !== initialItemCardsKeys.length) return true;
    if (currentItemCardsKeys.some((key, idx) => key !== initialItemCardsKeys[idx])) return true;
    if (currentItemCardsKeys.some(key => {
      const current = levelItemCards[key].sort();
      const initial = initialLevelItemCards[key].sort();
      if (current.length !== initial.length) return true;
      return current.some((item, idx) => item !== initial[idx]);
    })) return true;
    
    return false;
  }, [levelUpPoints, levelCoins, levelItemCards, initialLevelUpPoints, initialLevelCoins, initialLevelItemCards]);

  // 취소 핸들러
  const handleCancel = () => {
    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const finalLevelUpPoints = typeof levelUpPoints === 'number' ? levelUpPoints : 50;
      
      console.log('🔍 LevelSettingsModal: 저장할 데이터', {
        levelUpPoints: finalLevelUpPoints,
        selectedLevel,
        levelCoins,
        levelItemCards
      });
      
      await userDataService.saveLevelSettings(userId, classId, {
        levelUpPoints: finalLevelUpPoints,
        selectedLevel,
        levelCoins,
        levelItemCards
      });
      
      console.log('✅ LevelSettingsModal: 저장 완료');
      onClose();
    } catch (error) {
      console.error('레벨 설정 저장 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <LevelSettingsModalOverlay onClick={handleCancel}>
      <LevelSettingsModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>레벨 설정</h3>
          <CloseButton onClick={handleCancel}>×</CloseButton>
        </ModalHeader>
        
        <LevelSettingsBody>
          <LevelSettingsSection>
            <LevelSettingsSectionTitle>기본 설정</LevelSettingsSectionTitle>
            <LevelSettingsFormGroup>
              <LevelSettingsLabel>레벨업 포인트</LevelSettingsLabel>
              <LevelPointInputWrapper>
              <LevelPointMinusButton onClick={() => {
                const current = typeof levelUpPoints === 'number' ? levelUpPoints : 50;
                const baseUnit = defaultPoints; // 클래스의 기본 포인트 단위
                if (current > baseUnit) {
                  setLevelUpPoints(current - baseUnit);
                }
              }}>
                −
              </LevelPointMinusButton>
              <LevelPointInput
                type="number"
                value={levelUpPoints}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setLevelUpPoints('');
                  } else {
                    setLevelUpPoints(parseInt(value) || 0);
                  }
                }}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => {
                  if (e.target.value === '' || levelUpPoints === '') {
                    setLevelUpPoints(50);
                  }
                }}
                min="1"
                max="1000"
                placeholder="포인트"
              />
              <LevelPointPlusButton onClick={() => {
                const current = typeof levelUpPoints === 'number' ? levelUpPoints : 50;
                const baseUnit = defaultPoints; // 클래스의 기본 포인트 단위
                setLevelUpPoints(current + baseUnit);
              }}>
                +
              </LevelPointPlusButton>
              </LevelPointInputWrapper>
              <LevelSettingsDescription>
              설정한 포인트 단위로 레벨이 올라갑니다. (예: {defaultPoints}포인트 설정 시 {defaultPoints}점마다 레벨업)
              </LevelSettingsDescription>
            </LevelSettingsFormGroup>
          </LevelSettingsSection>

          <LevelSettingsDivider />

          <LevelSettingsSection>
            <LevelSettingsSectionTitle>레벨별 보상 설정</LevelSettingsSectionTitle>
            <LevelSelectRow>
              <LevelSelectMinusButton onClick={() => {
                const current = selectedLevel || 1;
                if (current > 1) {
                  setSelectedLevel(current - 1);
                }
              }}>
                &lt;
              </LevelSelectMinusButton>
              {isEditingLevel ? (
                <LevelSelectInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={editingLevelValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      setEditingLevelValue(val);
                    }
                  }}
                  onBlur={commitLevelEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      commitLevelEdit();
                    }
                  }}
                  autoFocus
                />
              ) : (
                <LevelSelectDisplay
                  $level={selectedLevel || 1}
                  onClick={startLevelEdit}
                >
                  {selectedLevel || 1}레벨
                </LevelSelectDisplay>
              )}
              <LevelSelectPlusButton onClick={() => {
                const current = selectedLevel || 1;
                setSelectedLevel(current + 1);
              }}>
                &gt;
              </LevelSelectPlusButton>
            </LevelSelectRow>

            {selectedLevel !== null && (
              <LevelRewardContainer $level={selectedLevel}>
                <LevelRewardSubTitle>{selectedLevel}레벨 보상 코인</LevelRewardSubTitle>
                <LevelCoinRow>
                  <LevelCoinMinusButton
                    onClick={() => {
                      const current = levelCoins[selectedLevel] || 0;
                      const step = coinDefaultPoints;
                      setLevelCoins({
                        ...levelCoins,
                        [selectedLevel]: Math.max(0, current - step)
                      });
                    }}
                  >
                    −
                  </LevelCoinMinusButton>
                  <LevelCoinDisplay>
                    {levelCoins[selectedLevel] || 0}코인
                  </LevelCoinDisplay>
                  <LevelCoinPlusButton
                    onClick={() => {
                      const current = levelCoins[selectedLevel] || 0;
                      const step = coinDefaultPoints;
                      setLevelCoins({
                        ...levelCoins,
                        [selectedLevel]: current + step
                      });
                    }}
                  >
                    +
                  </LevelCoinPlusButton>
                </LevelCoinRow>

                <LevelRewardSubTitle>{selectedLevel}레벨 보상 아이템</LevelRewardSubTitle>
                {currentUser?.userType === 'free' || currentUser?.userType === 'basic' ? (
                  <EmptyItemCardMessage>
                    프리/베이직 플랜에서는 레벨별 아이템 설정을 사용할 수 없습니다.
                  </EmptyItemCardMessage>
                ) : availableItemCards.length > 0 ? (
                  <ItemCardSelectGrid>
                    {availableItemCards.map((item) => {
                      const isSelected = (levelItemCards[selectedLevel] || []).includes(item.itemCardId);
                      return (
                        <ItemCardSelectBlock
                          key={item.itemCardId}
                          $isSelected={isSelected}
                          onClick={() => {
                            const currentItems = levelItemCards[selectedLevel] || [];
                            if (isSelected) {
                              // 제거
                              setLevelItemCards({
                                ...levelItemCards,
                                [selectedLevel]: currentItems.filter(id => id !== item.itemCardId)
                              });
                            } else {
                              // 추가
                              setLevelItemCards({
                                ...levelItemCards,
                                [selectedLevel]: [...currentItems, item.itemCardId]
                              });
                            }
                          }}
                        >
                          <ItemCardIconArea>
                            {item.cardImage === 'default' ? (
                              <PlaceholderIcon>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                  <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                  <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                </svg>
                              </PlaceholderIcon>
                            ) : item.cardImage && /^\d+$/.test(item.cardImage) ? (
                              <img src={`/CardIcon/IconBasic/IconBasic-${item.cardImage}.png`} alt={item.name} />
                            ) : item.cardImage ? (
                              <img src={item.cardImage} alt={item.name} />
                            ) : item.icon ? (
                              <img src={item.icon} alt={item.name} />
                            ) : (
                              <PlaceholderIcon>
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                                  <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                                </svg>
                              </PlaceholderIcon>
                            )}
                          </ItemCardIconArea>
                          <ItemCardName>{item.name}</ItemCardName>
                          <ItemCardMemo>{item.memo}</ItemCardMemo>
                          {((item.cost !== 'Nocharge') || (item.type === 'active' && item.useCount !== undefined)) && (
                            <ItemCardMetaGroup>
                              {item.cost !== 'Nocharge' && (
                                <ItemCardCost>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                    <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                    <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                  </svg>
                                  <span>X{item.cost}</span>
                                </ItemCardCost>
                              )}
                              {item.type === 'active' && item.useCount !== undefined && (
                                <ItemCardUseCount>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                  </svg>
                                  <span>X{item.useCount}</span>
                                </ItemCardUseCount>
                              )}
                            </ItemCardMetaGroup>
                          )}
                          {isSelected && (
                            <SelectedBadge>✓</SelectedBadge>
                          )}
                        </ItemCardSelectBlock>
                      );
                    })}
                  </ItemCardSelectGrid>
                ) : (
                  <EmptyItemCardMessage>
                    레벨업 아이템이 없습니다. 아이템카드 설정에서 먼저 레벨업 아이템을 생성해주세요.
                  </EmptyItemCardMessage>
                )}
              </LevelRewardContainer>
            )}
          </LevelSettingsSection>
        </LevelSettingsBody>
        
        <LevelSettingsModalFooter>
          <LevelSettingsCloseButton data-variant="modal-secondary" onClick={handleCancel}>
            닫기
          </LevelSettingsCloseButton>
          <LevelSettingsSaveButton onClick={handleSave} disabled={isLoading}>
            {isLoading ? '저장 중...' : '저장'}
          </LevelSettingsSaveButton>
        </LevelSettingsModalFooter>
      </LevelSettingsModalContent>
    </LevelSettingsModalOverlay>

    {/* 취소 확인 모달 */}
    {showCancelConfirm && (
      <div onClick={(e) => e.stopPropagation()}>
        <ConfirmModal
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={() => {
            setShowCancelConfirm(false);
            onClose();
          }}
          onCancel={() => {
            setShowCancelConfirm(false);
            handleSave();
          }}
          title={[
            "변경사항이 있습니다.",
            "저장하지 않고 나가시겠습니까?",
            "변경사항이 모두 사라집니다."
          ]}
          confirmText="나가기"
          cancelText="저장"
          icon="!"
          iconBackground="var(--status-exempted)"
        />
      </div>
    )}
    </>
  );
};
